import { actions as sessionActions } from '../../session';
import { actions as formActions } from '../../form';
import { reduxActions } from '../actions';

import { fetchWithOptions } from '../../../utility/fetch';
import { parseJWTFromCookie, getJWTData } from '../../../utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog } from '../../../utility/logger';
import { omitControlProps } from '../../../utility/documentHandling';
import { lensPath, set } from 'ramda';
import { INeuroCommit, INeuroDocument } from 'neuro-data-structures';
import { path, take } from 'Utility/ramdaReplacement';
import { TDispatch } from 'Store/index';

const refreshJWT = (): void => {
  // Refresh JWT if hour or less left on expiry
  const refreshWhen = 60 * 60 * 1000; // Minutes, seconds, milliseconds => 2 hrs
  const jwt = getJWTData();
  if (jwt && jwt.exp - refreshWhen < Date.now()) {
    sessionActions.refreshJWT();
  }
};

const createDummyDocument = (
  documentType: string,
  documentId: string,
  creatorId: string,
  creatorOrg: string,
): Partial<INeuroDocument> => ({
  documentId,
  documentType,
  createDate: Date.now(),
  creatorId,
  creatorOrg,
  ownerOrg: creatorOrg,
  removeTS: null,
  commits: [],
});
const createDummyCommit = (
  commitId: string,
  creatorId: string,
  creatorOrg: string,
  data: TAnyObject,
  commitDate?: number,
): INeuroCommit => ({
  commitId,
  createDate: Date.now(),
  creatorId,
  commitDate: commitDate ? commitDate : null,
  creatorOrg,
  data,
  source: null,
  lockedFor: null,
});

/**
 * Create a new commit
 */
export const createNewCommit = async (
  editingDocument: { name: string; id: string },
  dispatch: TDispatch,
  formData?: TAnyObject & IControlProps,
): Promise<string> => {
  const useruuid = getJWTData()?.useruuid || null;
  const orgid = getJWTData()?.orgid || null;

  const data = formData && omitControlProps(formData);

  return await fetchWithOptions(
    `/api/documents/${editingDocument.name}/${editingDocument.id}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: JSON.stringify(data) },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(async json => {
      if (json !== false) {
        const newCommitData = createDummyCommit(json.commitId, useruuid || '', orgid || '', data || {});

        // Create new commit in redux
        dispatch(reduxActions.newCommitAction(editingDocument.id, newCommitData));
        return json.commitId;
      }
      throw new Error('missing commit ID');
    })
    .catch(error => makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'POST new commit init')));
};

/**
 * Update existing commit
 */
export const updateExistingCommit = (
  editingDocument: { name: string; id: string },
  commitId: string,
  dispatch: TDispatch,
  formData: TAnyObject & IControlProps,
  signal?: AbortSignal,
  newCommitCreation = false,
): Promise<string | void> => {
  refreshJWT();
  // Save commit

  const data = omitControlProps(formData);

  return fetchWithOptions(
    `/api/documents/${editingDocument.name}/${editingDocument.id}/${commitId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PATCH', body: JSON.stringify(data), signal: signal, propagateAbortError: true },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res;
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(() => {
      // Update redux store
      !newCommitCreation && dispatch(reduxActions.updateCommitAction(editingDocument.id, commitId, false, data));
      return 'Update successful';
    })
    .catch((error: Error) => {
      // Check for AbortError and handle it gracefully
      if (error.name === 'AbortError') {
        return; // Suppress the error and end the promise chain
      }
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'PATCH existing commit'));
    });
};

/**
 * Close / finish commit
 * 1. Close commit with data (body)
 * 2. Dispatch changes to redux
 */
export const updateCommitAndClose = async (
  editingDocument: { name: string; id: string },
  commitId: string,
  dispatch: TDispatch,
  formData: TAnyObject & IControlProps,
): Promise<void> => {
  const data = omitControlProps(formData);

  // Close commit
  return await fetchWithOptions(
    `/api/documents/${editingDocument.name}/${editingDocument.id}/${commitId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: JSON.stringify(data) },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        // Update redux store
        dispatch(reduxActions.updateCommitAction(editingDocument.id, commitId, true, data));
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .catch(error =>
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'POST commit close with data finished')),
    );
};

/**
 * Cancels open commit based on commit id
 */
export const deleteExistingCommit = async (
  editingDocument: { name: string; id: string },
  commitId: string,
  dispatch: TDispatch,
): Promise<void> => {
  return await fetchWithOptions(
    `/api/documents/${editingDocument.name}/${editingDocument.id}/${commitId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'DELETE' },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res;
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(() => {
      // Update docs in redux
      dispatch(reduxActions.cancelCommitAction(editingDocument.id, commitId));
    })
    .catch(err => makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'DELETE existing commit')));
};

/**
 * Delete document
 */
export const deleteExistingDocument = async (
  editingDocument: { name: string; id: string },
  dispatch: TDispatch,
  reason: { [key: string]: string | PartialDate },
  forced = false,
  currentUser: string,
  currentOrg: string,
): Promise<string | void> => {
  return await fetchWithOptions(
    `/api/documents/${editingDocument.name}/${editingDocument.id}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'DELETE', body: JSON.stringify({ reason, date: Date.now(), forced }) },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res;
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(() => {
      // Update docs in redux
      dispatch(
        reduxActions.deleteDocumentAction(editingDocument.id, {
          removeDate: new Date().toUTCString(),
          remover: currentUser,
          removerOrg: currentOrg,
          removeReason: reason,
        }),
      );
      return 'Document deleted';
    })
    .catch(error =>
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'DELETE existing document')),
    );
};

/**
 * Create a new document
 */
export const createNewDocument = async (
  name: string,
  dispatch: TDispatch,
  /** Use noFormUpdate if we want to create a new document, but not have it affect any currently being edited forms etc */
  noFormUpdate = false,
  formData?: TAnyObject & IControlProps,
): Promise<{ _id?: string; _cid?: string } | void> => {
  const useruuid = getJWTData()?.useruuid || null;
  const orgid = getJWTData()?.orgid || null;

  return await fetchWithOptions(`/api/documents/${name}`, { neurojwt: parseJWTFromCookie() }, { method: 'POST' })
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(async json => {
      if (json !== false) {
        // Update document store as well with new document
        const data = createDummyDocument(name, json.documentId, useruuid || '', orgid || '');

        dispatch(reduxActions.newDocumentAction(data));
        // Create new commit
        const cid = await createNewCommit({ name, id: json.documentId }, dispatch, formData);

        // We can set editing document now that we have document id
        if (!noFormUpdate && json.documentId && cid) {
          dispatch(formActions.setEditing(name, json.documentId));
        }
        return { _id: json.documentId, _cid: cid };
      } else {
        throw new Error('Document was not created');
      }
    })
    .catch(error =>
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'POST create new document')),
    );
};

/**
 * Put a new document with data
 * This doesn't really work at the moment as the put fetch should return at least the document id for the newly created document
 * Currently it returns no JSON and procudes an error, though the document is created
 */
export const putNewDocument = async (
  name: string,
  dispatch: TDispatch,
  formData: TAnyObject,
): Promise<{ _id?: string; _cid?: string } | void> => {
  const useruuid = getJWTData()?.useruuid || null;
  const orgid = getJWTData()?.orgid || null;

  return await fetchWithOptions(
    `/api/documents/${name}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PUT', body: JSON.stringify(formData) },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(async json => {
      if (json !== false) {
        // Update document store as well with new document
        let data = createDummyDocument(name, json.documentId, useruuid || '', orgid || '');
        data = {
          ...data,
          commits: [
            createDummyCommit(json.commitId, useruuid || '', orgid || '', formData, Date.now()) as INeuroCommit,
          ],
        };

        dispatch(reduxActions.newDocumentAction(data));
      } else {
        throw new Error('Document was not put');
      }
    })
    .catch(error =>
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'PUT create new document')),
    );
};

const parseFieldPathAndPatch = (
  formData: TAnyObject,
  fieldPath: string,
  updateValue: any,
  collectionInfo?: { id: string; idFieldName: string },
) => {
  const fields = fieldPath.split('.');
  const collectionFieldNameIdx = fields.findIndex(f => f === '$') - 1;

  let lensPathArr: Array<string | number> = fields;
  if (collectionInfo && (collectionFieldNameIdx || collectionFieldNameIdx === 0)) {
    const collection = path(take(collectionFieldNameIdx + 1, fields), formData) as [{ [key: string]: string }];
    const collectionItemIndex = collection.findIndex(c => c[collectionInfo?.idFieldName] === collectionInfo.id);
    lensPathArr = fields.map(f => (f === '$' ? collectionItemIndex : f));
  }
  const newFormData = set(lensPath(lensPathArr), updateValue, formData);
  return newFormData;
};

/**
 * Change a field in a document through different api (no need to create a commit)
 * This should only be used to change fields in locked documents
 */
export const changeFieldInDocument = (
  editingDocument: { name: string; id: string },
  dispatch: TDispatch,
  formData: TAnyObject & IControlProps,
  newData: TAnyObject,
  fieldLocked: boolean,
  collectionInfo?: { id: string; idFieldName: string },
) => {
  const useruuid = getJWTData()?.useruuid || null;
  const orgid = getJWTData()?.orgid || null;
  if (!useruuid || !orgid) return;

  const newDataKeys = Object.keys(newData);

  let body: TAnyObject | TAnyObject[] = {};
  if (newDataKeys.length === 1) {
    body = {
      fieldPath: newDataKeys[0], // Field path to update (e.g. "foo.bar.$.test"")
      updateValue: newData[newDataKeys[0]], // The value to which the field should be set
      lookupField: collectionInfo?.idFieldName, // Field to use for looking up correct object in a collection (e.g. "id")
      lookupValue: collectionInfo?.id, // Value to match (e.g. "123")
    };
  } else {
    body = newDataKeys.map(key => ({ fieldPath: key, updateValue: newData[key] }));
  }

  return fetchWithOptions(
    `api/fieldpatch/${editingDocument.name}/${editingDocument.id}`,
    { neurojwt: parseJWTFromCookie() },
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        const newCommitData: INeuroCommit = {
          commitId: 'newDocumentCommitID',
          createDate: Date.now(),
          creatorId: useruuid,
          commitDate: Date.now(),
          creatorOrg: orgid,
          source: null,
          lockedFor: null,
          data:
            newDataKeys.length === 1
              ? omitControlProps({
                  ...(parseFieldPathAndPatch(
                    formData,
                    newDataKeys[0],
                    newData[newDataKeys[0]],
                    collectionInfo,
                  ) as TAnyObject & IControlProps),
                })
              : omitControlProps({ ...formData, ...newData }),
        };
        if (fieldLocked) dispatch(reduxActions.modifyLockedFieldAction(editingDocument.id, newCommitData));
        else dispatch(reduxActions.modifyFieldAction(editingDocument.id, newCommitData));
        return true;
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'PATCH change field in existing document'));
      return false;
    });
};

/**
 * Lifecare sync
 */
export const lifecareSync = async (): Promise<boolean> => {
  return await fetchWithOptions(`/api/v1/lifecare/diagnosis`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then((res: Response) => {
      if (res.status === 200) {
        return true;
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .catch(error => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET Lifecare sync'));
      return false;
    });
};

/**
 * Get single document by type and id
 */
export const getDocument = async (
  documentType: string,
  documentId: string,
  /** Document get/update purpose string for better tracking and logging */
  getPurpose: string,
): Promise<INeuroDocument | false> => {
  return await fetchWithOptions(
    `/api/documents/${documentType}/${documentId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then((res: INeuroDocument) => {
      return res;
    })
    .catch(error => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET document for ' + getPurpose));
      return false;
    });
};

export const undeleteDocument = async (
  documentType: string,
  documentId: string,
  dispatch: TDispatch,
): Promise<string | void> => {
  const restoreTS = Date.now();
  return await fetchWithOptions(
    `/api/document-undelete/${documentType}/${documentId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PATCH' },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res;
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(() => {
      const jwtData = getJWTData();
      if (!(jwtData?.useruuid && jwtData?.orgid)) return;
      dispatch(reduxActions.undeleteDocumentAction(documentId, restoreTS, jwtData.useruuid, jwtData.orgid));
      return `Document '${documentId}' restored`;
    })
    .catch(error => makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'UNDELETE document')));
};

const queryStringBuilder = (data: Record<string, string>) => {
  const keys: Array<keyof typeof data> = Object.keys(data);

  let query = '';
  keys.forEach((key, i) => {
    if (i === 0) {
      query = `?${key}=${data[key]}`;
    } else {
      query = query + `&${key}=${data[key]}`;
    }
  });
  return query;
};

// Get file blob by id
export const getFile = async (documentId: string, fileContentId: string): Promise<Blob | false> => {
  return await fetchWithOptions(
    `/api/files/v1/${documentId}/${fileContentId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.blob();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(res => {
      return res;
    })
    .catch(error => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET file blob'));
      return false;
    });
};

export const sanitizeName = (name: string) => name.replace(/\?|\&|\=|\//g, '#');

// Create new file
export const postFile = async (
  file: File,
  name: string,
  date: string,
  sub_type: string,
  reference_type?: string,
): Promise<INeuroDocument | false> => {
  const refType: { reference_type: string } | {} = reference_type ? { reference_type: reference_type } : {};
  const queryParams = queryStringBuilder({ name: sanitizeName(name), date, sub_type, ...refType });

  return await fetchWithOptions(
    `/api/files/v1/${queryParams}`,
    { neurojwt: parseJWTFromCookie(), 'Content-Type': file.type },
    { method: 'POST', body: file },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(async (res: { documentId: string; commitId: string }) => {
      return await getDocument('filestorefile', res.documentId, 'file data update after posting new file').then(res => {
        if (res) return res;
        return false;
      });
    })
    .catch(error => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'POST file and metadata'));
      return false;
    });
};

// Change file content
export const postFileContent = async (file: File, documentId: string): Promise<INeuroDocument | false> => {
  return await fetchWithOptions(
    `/api/files/v1/${documentId}`,
    { neurojwt: parseJWTFromCookie(), 'Content-Type': file.type },
    { method: 'POST', body: file },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(async () => {
      return await getDocument('filestorefile', documentId, 'file data update after posting new file content').then(
        res => {
          if (res) return res;
          return false;
        },
      );
    })
    .catch(error => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'POST file and metadata'));
      return false;
    });
};

// Edit file metadata
export const patchFileMetadata = async (
  documentId: string,
  data: Partial<Pick<TFILE_METADATA, 'date' | 'name' | 'subType' | 'referenceType'>>,
): Promise<INeuroDocument | false> => {
  const name = data.name ? { name: sanitizeName(data.name) } : {};

  return await fetchWithOptions(
    `/api/files/v1/${documentId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PATCH', body: JSON.stringify({ ...data, ...name }) },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return true;
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then(async () => {
      return await getDocument('filestorefile', documentId, 'file data update after updating file metadata').then(
        res => {
          if (res) return res;
          return false;
        },
      );
    })
    .catch(error => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'Patch existing file metadata'));
      return false;
    });
};

// Delete file
export const deleteFile = async (
  id: string,
  reason: { date: PartialDate; reason: string; reasonOther?: string },
): Promise<{ date: number; reason: { date: PartialDate; reason: string; reasonOther?: string } } | false> => {
  return await fetchWithOptions(
    `/api/files/v1/${id}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'DELETE', body: JSON.stringify({ reason, date: Date.now() }) },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return { reason, date: Date.now() };
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .catch(error => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'Delete existing file'));
      return false;
    });
};
